/**
 * Cmd-K command palette. Modal opened by Cmd+K / Ctrl+K.
 *
 * Items = navigation + light actions (copy primary link, request payout, contact support).
 * Fuzzy matching is a simple case-insensitive substring/character scan.
 * Recent items (max 5) persisted in localStorage under `partner.cmdk.recent`.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from '@tanstack/react-router';
import { createPortal } from 'react-dom';
import { Search, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { partnerApi } from '@/lib/api-affiliate';

interface CmdItem {
  id: string;
  label: string;
  hint?: string;
  kind: 'nav' | 'action';
  to?: string;
  run?: () => void | Promise<void>;
}

const RECENT_KEY = 'partner.cmdk.recent';
const RECENT_MAX = 5;

function readRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed.filter((s): s is string => typeof s === 'string')) : [];
  } catch {
    return [];
  }
}
function writeRecent(ids: string[]): void {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(ids.slice(0, RECENT_MAX)));
  } catch {
    /* ignore */
  }
}

function fuzzyMatch(haystack: string, needle: string): boolean {
  if (!needle) return true;
  const h = haystack.toLowerCase();
  const n = needle.toLowerCase();
  if (h.includes(n)) return true;
  let i = 0;
  for (let j = 0; j < h.length && i < n.length; j++) {
    if (h[j] === n[i]) i++;
  }
  return i === n.length;
}

interface CmdKProps {
  open: boolean;
  onClose: () => void;
}

export function CmdK({ open, onClose }: CmdKProps) {
  const router = useRouter();
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const links = partnerApi.links.useList();

  const items = useMemo<CmdItem[]>(() => {
    const primaryLink = links.data?.items?.[0];
    const trackingUrl = primaryLink?.trackingUrl ?? '';

    const list: CmdItem[] = [
      { id: 'nav.dashboard', label: 'Дашборд', hint: 'Перейти', kind: 'nav', to: '/' },
      { id: 'nav.offers', label: 'Офферы', hint: 'Перейти', kind: 'nav', to: '/offers' },
      { id: 'nav.links', label: 'Ссылки', hint: 'Перейти', kind: 'nav', to: '/links' },
      { id: 'nav.conversions', label: 'Конверсии', hint: 'Перейти', kind: 'nav', to: '/conversions' },
      { id: 'nav.balance', label: 'Баланс', hint: 'Перейти', kind: 'nav', to: '/balance' },
      { id: 'nav.payouts', label: 'Выплаты', hint: 'Перейти', kind: 'nav', to: '/payouts' },
      { id: 'nav.profile', label: 'Профиль', hint: 'Перейти', kind: 'nav', to: '/profile' },
    ];
    if (trackingUrl) {
      list.push({
        id: 'action.copy_primary_link',
        label: 'Скопировать основную ссылку',
        hint: 'Действие',
        kind: 'action',
        run: async () => {
          try {
            await navigator.clipboard.writeText(trackingUrl);
            toast.success('Ссылка скопирована');
          } catch {
            toast.error('Не удалось скопировать');
          }
        },
      });
    }
    list.push({
      id: 'action.request_payout',
      label: 'Запросить выплату',
      hint: 'Действие',
      kind: 'action',
      to: '/payouts',
    });
    list.push({
      id: 'action.contact_support',
      label: 'Связаться с поддержкой',
      hint: 'Действие',
      kind: 'action',
      run: () => {
        window.open('mailto:support@nexus-crm.io', '_blank');
      },
    });
    return list;
  }, [links.data]);

  const filtered = useMemo<CmdItem[]>(() => {
    const recent = readRecent();
    const matches = items.filter((it) => fuzzyMatch(it.label, query));
    if (!query) {
      // sort recent first.
      const recentSet = new Set(recent);
      return [
        ...recent
          .map((id) => matches.find((m) => m.id === id))
          .filter((x): x is CmdItem => !!x),
        ...matches.filter((m) => !recentSet.has(m.id)),
      ];
    }
    return matches;
  }, [items, query]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      // delay until rendered.
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx((i) => Math.max(0, i - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const item = filtered[activeIdx];
        if (item) runItem(item);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (!open) return null;

  function runItem(item: CmdItem) {
    const recent = readRecent().filter((id) => id !== item.id);
    recent.unshift(item.id);
    writeRecent(recent);
    onClose();
    if (item.run) {
      void item.run();
    }
    if (item.to) {
      router.navigate({ to: item.to as '/' });
    }
  }

  const modal = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Командная панель"
      className="fixed inset-0 z-[100] flex justify-center items-start pt-[14vh] px-4"
      style={{ animation: 'cmdk-overlay-in 0.18s var(--ease-out)' }}
    >
      <button
        type="button"
        aria-label="Закрыть"
        onClick={onClose}
        className="absolute inset-0 bg-[var(--bg-overlay)] backdrop-blur-sm"
      />
      <div
        className="relative w-full max-w-[560px] rounded-[var(--radius-lg)] border border-[var(--border-strong)] bg-[var(--bg-raised)] shadow-[var(--shadow-lg)] overflow-hidden"
        style={{ animation: 'cmdk-pop-in 0.24s var(--ease-spring)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-3 h-12 border-b border-[var(--border)]">
          <Search size={16} className="text-[var(--text-muted)]" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIdx(0);
            }}
            placeholder="Поиск страниц и действий…"
            className="flex-1 h-full bg-transparent outline-none focus:outline-none focus-visible:shadow-none text-[14px] text-[var(--text)] placeholder:text-[var(--text-quiet)]"
          />
          <kbd className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-quiet)] border border-[var(--border)] rounded px-1.5 py-0.5">
            Esc
          </kbd>
        </div>
        <div className="max-h-[60vh] overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <div className="px-4 py-8 text-center text-[13px] text-[var(--text-muted)]">
              Ничего не найдено
            </div>
          ) : (
            <ul role="listbox">
              {filtered.map((it, idx) => {
                const active = idx === activeIdx;
                return (
                  <li key={it.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={active}
                      onMouseEnter={() => setActiveIdx(idx)}
                      onClick={() => runItem(it)}
                      className={
                        'w-full text-left flex items-center gap-3 px-3 h-10 text-[13px] ' +
                        (active
                          ? 'bg-[var(--accent-soft)] text-[var(--text)]'
                          : 'text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)]')
                      }
                    >
                      <span className="flex-1 truncate">{it.label}</span>
                      {it.hint && (
                        <span className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-muted)]">
                          {it.hint}
                        </span>
                      )}
                      <ArrowRight size={13} className="text-[var(--text-quiet)]" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
      <style>{`
        @keyframes cmdk-overlay-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes cmdk-pop-in {
          from { opacity: 0; transform: translateY(-8px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0)    scale(1); }
        }
      `}</style>
    </div>
  );

  return createPortal(modal, document.body);
}
